import { z } from "zod";
import { Persistence } from "../../config/constants";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { StoryProgress } from "../../game/GameState";
import { STORY_SCENE_IDS, StorySceneId } from "../../game/story";
import {
  registerSerializable,
  SerializableEntity,
} from "../save/serialization";
import { TimeFlow } from "../TimeFlow";

/**
 * The story's ledger: which scenes have played (saved, one-way) and
 * which one is playing right now (transient — a reload lands with no
 * scene up, and the one that was interrupted is still owed, so it
 * simply plays again from the top).
 *
 * A playing scene holds the world still: this entity registers the
 * TimeFlow stop, so the clock is stopped for as long as the box is up
 * or she is walking, in the shell and in the headless driver alike. The
 * keys are the shell's to take (the CutsceneDirector and the input
 * entities read `playing`).
 *
 * Scenes are begun and finished through story-commands.ts; nothing else
 * writes here.
 */

const sceneIdSchema = z.enum(
  STORY_SCENE_IDS as unknown as [StorySceneId, ...StorySceneId[]],
);

const schema = z.object({
  scenesPlayed: z.array(sceneIdSchema),
});

type StoryData = z.infer<typeof schema>;

export class StoryTracker
  extends BaseEntity
  implements Entity, SerializableEntity
{
  readonly saveType = "story";
  id = "story";
  persistenceLevel: number = Persistence.Game;

  scenesPlayed: StorySceneId[];
  /** The scene up right now, or null. Never saved. */
  playing: StorySceneId | null = null;

  private unregisterStop: (() => void) | null = null;

  constructor(data: Partial<StoryData> = {}) {
    super();
    this.scenesPlayed = [...(data.scenesPlayed ?? [])];
  }

  /** The shared data shape (projection, serialization). */
  get progress(): StoryProgress {
    return { scenesPlayed: this.scenesPlayed };
  }

  @on("afterAdded")
  onAfterAdded() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    if (timeFlow) {
      this.unregisterStop = timeFlow.registerStop(() => this.playing !== null);
    }
  }

  @on("destroy")
  onDestroy() {
    this.unregisterStop?.();
    this.unregisterStop = null;
  }

  toJSON(): StoryData {
    return { scenesPlayed: [...this.scenesPlayed] };
  }
}

registerSerializable({
  type: "story",
  singleton: true,
  schema,
  fromJSON: (data) => new StoryTracker(data),
});
